/**
 * Reduces per-frame `requestVideoFrameCallback` metadata into one `FramePipelineSummary` per
 * stats tick.
 *
 * Every presented frame the DecodeHintCollector sees is observed here; the congestion monitor
 * drains the summary on the tick that sends `clientStats`. The three durations are the
 * decomposition that separates the standing-buffer hypotheses: receive→present is buffer
 * plus decode, `processingDuration` is the decode alone, present→display is the compositor.
 *
 * Units: rVFC metadata `receiveTime`, `expectedDisplayTime` and `presentationTime`
 * are DOMHighResTimeStamps in ms; `processingDuration` is in SECONDS and is converted here.
 * Browser-free: fed plain observations, so tests drive it directly.
 */

import type { LatestRvfcObservation } from '../decodeHintCollector';
import type { FramePipelineSummary } from './framePipelineContracts';

/** Percentiles are nearest-rank on the sorted, non-empty samples (no interpolation). */
function nearestRank(sorted: number[], percentile: number): number {
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil((percentile / 100) * sorted.length)));
  return sorted[rank - 1] ?? 0;
}

function roundMs(value: number): number {
  return Math.round(value * 10) / 10;
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export class FramePipelineSampler {
  private receiveToPresentMs: number[] = [];
  private processingMs: number[] = [];
  private presentToDisplayMs: number[] = [];
  private sampleCount = 0;

  /**
   * Record one presented frame. A frame counts as a sample when it carries `receiveTime`;
   * frames without it (an engine that does not expose it, or a metadata-less callback)
   * contribute nothing, so an empty tick reads as "unmeasured" rather than as zero latency.
   */
  observe(observation: LatestRvfcObservation): void {
    if (!finiteNumber(observation.receiveTime)) return;
    this.sampleCount += 1;
    if (finiteNumber(observation.presentationTime)) {
      this.receiveToPresentMs.push(observation.presentationTime - observation.receiveTime);
      if (finiteNumber(observation.expectedDisplayTime)) {
        this.presentToDisplayMs.push(observation.expectedDisplayTime - observation.presentationTime);
      }
    }
    if (finiteNumber(observation.processingDuration)) {
      this.processingMs.push(observation.processingDuration * 1000);
    }
  }

  /**
   * The tick's summary, then start the next tick empty. Undefined when no frame was sampled,
   * so the wire field is omitted rather than zero-filled.
   */
  summarizeAndReset(): FramePipelineSummary | undefined {
    if (this.sampleCount === 0) return undefined;
    const receiveToPresent = [...this.receiveToPresentMs].sort((a, b) => a - b);
    const processing = [...this.processingMs].sort((a, b) => a - b);
    const presentToDisplay = [...this.presentToDisplayMs].sort((a, b) => a - b);
    const summary: FramePipelineSummary = {
      sampleCount: this.sampleCount,
      ...(receiveToPresent.length > 0
        ? {
            receiveToPresentP50Ms: roundMs(nearestRank(receiveToPresent, 50)),
            receiveToPresentP95Ms: roundMs(nearestRank(receiveToPresent, 95)),
          }
        : {}),
      ...(processing.length > 0 ? { processingP50Ms: roundMs(nearestRank(processing, 50)) } : {}),
      ...(presentToDisplay.length > 0 ? { presentToDisplayP50Ms: roundMs(nearestRank(presentToDisplay, 50)) } : {}),
    };
    this.reset();
    return summary;
  }

  /** Discard the current tick's samples without producing a summary. */
  reset(): void {
    this.receiveToPresentMs = [];
    this.processingMs = [];
    this.presentToDisplayMs = [];
    this.sampleCount = 0;
  }
}
